import React, { useState, useEffect } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { MdAccessTime, MdPause } from 'react-icons/md';

interface LiveMatchTimerProps {
  matchStatus: string;
  isLive: boolean;
  size?: 'small' | 'medium' | 'large';
  showIcon?: boolean;
}

export function LiveMatchTimer({ 
  matchStatus, 
  isLive, 
  size = 'medium',
  showIcon = true 
}: LiveMatchTimerProps) {
  const [currentMinute, setCurrentMinute] = useState<number>(0);
  const [isPaused, setIsPaused] = useState(false);

  const calculateMinute = (status: string): number => {
    if (status === 'Half Time') return 45;
    if (status === 'After ET') return 120;
    if (status.includes('Finished')) return 90;
    
    const minute = parseInt(status);
    return isNaN(minute) ? 0 : minute;
  };

  useEffect(() => {
    const initialMinute = calculateMinute(matchStatus);
    setCurrentMinute(initialMinute);
    setIsPaused(matchStatus === 'Half Time' || matchStatus.includes('Finished'));

    if (isLive && !isPaused) {
      const interval = setInterval(() => {
        setCurrentMinute(prev => {
          const newMinute = prev + 1;
          if (newMinute >= 45 && newMinute < 46) {
            setIsPaused(true);
            return 45;
          }
          if (newMinute >= 90) {
            setIsPaused(true);
            return 90;
          }
          return newMinute;
        });
      }, 60000); // Atualizar a cada minuto

      return () => clearInterval(interval);
    }
  }, [matchStatus, isLive, isPaused]);

  const getTimeDisplay = () => {
    if (matchStatus === 'Half Time') return "45' HT";
    if (matchStatus.includes('Finished')) return "FT";
    if (matchStatus === 'After ET') return "120' FT";
    
    if (currentMinute <= 45) {
      return `${currentMinute}'`;
    } else if (currentMinute <= 90) {
      return `${currentMinute}'`;
    } else {
      return `${currentMinute}' +${currentMinute - 90}`;
    }
  };

  const getBackgroundColor = () => {
    if (!isLive) return '#666';
    if (isPaused) return '#ffa726';
    if (currentMinute > 80) return '#ff4444';
    return '#49fbb0';
  };

  const getTextColor = () => {
    if (!isLive || isPaused) return 'white';
    return '#183530';
  };

  const getFontSize = () => {
    switch (size) {
      case 'small': return '0.75rem';
      case 'large': return '1.25rem';
      default: return '0.875rem';
    }
  };

  const getIconSize = () => {
    switch (size) {
      case 'small': return 14;
      case 'large': return 20;
      default: return 16;
    }
  };

  if (size === 'small') {
    return (
      <Chip
        icon={showIcon ? (isPaused ? <MdPause size={12} /> : <MdAccessTime size={12} />) : undefined}
        label={getTimeDisplay()}
        size="small"
        sx={{
          backgroundColor: getBackgroundColor(),
          color: getTextColor(),
          fontWeight: 'bold',
          fontSize: getFontSize(),
          '& .MuiChip-icon': {
            color: getTextColor()
          },
          ...(isLive && !isPaused && {
            animation: 'pulse 2s infinite'
          })
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: size === 'large' ? 2 : 1.5,
        py: size === 'large' ? 1 : 0.5,
        backgroundColor: getBackgroundColor(),
        borderRadius: '8px',
        ...(isLive && !isPaused && {
          animation: 'pulse 2s infinite'
        })
      }}
    >
      {showIcon && (
        isPaused ? 
          <MdPause size={getIconSize()} color={getTextColor()} /> :
          <MdAccessTime size={getIconSize()} color={getTextColor()} />
      )}
      <Typography
        variant="body2"
        sx={{
          color: getTextColor(),
          fontWeight: 'bold',
          fontSize: getFontSize(),
          lineHeight: 1
        }}
      >
        {getTimeDisplay()}
      </Typography>
    </Box>
  );
} 